<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { characterProgress } from './characterEvolution';
import { sprite } from './gameThemes';
const props = defineProps({
  xp: { type: Number, default: 0 },
  character: { type: String, default: 'guardian' },
  motion: { type: Boolean, default: true },
  accessory: { type: String, default: 'auto' },
  compact: { type: Boolean, default: false },
});
const progress = computed(() => characterProgress(props.xp, props.character));
const celebration = ref('');
let timer;
watch(
  () => props.xp,
  (xp, previous) => {
    clearTimeout(timer);
    celebration.value = '';
    if (xp <= previous) return;
    celebration.value =
      characterProgress(xp).level > characterProgress(previous).level
        ? 'Novo nível!'
        : `+${xp - previous} XP`;
    timer = setTimeout(() => {
      celebration.value = '';
    }, 3500);
  }
);
onBeforeUnmount(() => clearTimeout(timer));
const ornament = computed(() => {
  if (props.accessory === 'none') return null;
  if (progress.value.stage >= 2 && ['auto', 'crown'].includes(props.accessory))
    return 'i-hugeicons-crown';
  if (progress.value.stage >= 1 && ['auto', 'shield'].includes(props.accessory))
    return 'i-hugeicons-shield-01';
  return null;
});
</script>

<template>
  <span
    class="game-character"
    :class="{
      'is-compact': compact,
      'has-motion': motion,
      'is-celebrating': !!celebration,
    }"
  >
    <span class="character-platform" aria-hidden="true" />
    <img
      :src="sprite(progress.sprite)"
      class="game-hero"
      :alt="`${progress.characterName}, ${progress.name}, nível ${progress.level}`"
    />
    <span
      v-if="ornament"
      :class="ornament"
      class="character-ornament"
      aria-hidden="true"
    />
    <span v-if="!compact" class="character-celebration" role="status">{{
      celebration
    }}</span>
  </span>
</template>

<style scoped>
.game-character {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 112px;
  height: 112px;
  flex-shrink: 0;
}
.game-hero {
  width: 88px;
  height: 88px;
  object-fit: contain;
  image-rendering: pixelated;
  position: relative;
  z-index: 1;
}
.character-platform {
  position: absolute;
  bottom: 8px;
  width: 80px;
  height: 14px;
  border-radius: 50%;
  background: #0003;
}
.character-ornament {
  position: absolute;
  top: 0;
  right: 6px;
  width: 22px;
  height: 22px;
  color: #fbbf24;
  z-index: 2;
}
.character-celebration {
  position: absolute;
  top: -20px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 700;
  color: inherit;
}
.is-compact {
  width: 40px;
  height: 40px;
}
.is-compact .game-hero {
  width: 36px;
  height: 36px;
}
.is-compact .character-platform {
  bottom: 1px;
  width: 28px;
  height: 5px;
}
.is-compact .character-ornament {
  width: 12px;
  height: 12px;
  right: 0;
}
.has-motion .game-hero {
  animation: character-idle 3s ease-in-out infinite;
}
.has-motion.is-celebrating .game-hero {
  animation: character-celebrate 0.65s ease-in-out 3;
}
@keyframes character-idle {
  50% {
    transform: translateY(-3px);
  }
}
@keyframes character-celebrate {
  50% {
    transform: translateY(-12px) rotate(6deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .game-character.has-motion .game-hero,
  .game-character.has-motion.is-celebrating .game-hero {
    animation: none;
  }
}
</style>
